import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  Users,
  BarChart3,
  Target,
  Shield,
  CheckCircle,
  ArrowRight,
  Star,
  Building,
  Zap,
  DollarSign
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import Reveal from '@components/dimensional/Reveal'
import TiltCard from '@components/dimensional/TiltCard'
import RewardsStack3D from '@components/dimensional/RewardsStack3D'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import BandArchitecture from '@components/dimensional/BandArchitecture'

const HomePage = () => {
  const { content, loading } = useContent()

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  // Total Rewards Specialization - Core 6 comprehensive services for homepage display
  const services = [
    {
      id: 'equity',
      no: '01',
      icon: TrendingUp,
      title: 'Equity & Long-Term Incentives',
      description:
        'Option pools, RSU grant sizing, ESOP mechanics, cliff and vesting design. We model dilution and expected value before anyone signs a grant letter.',
      features: ['Stock options & RSUs', 'ESOP structuring', 'Pool & dilution modelling', 'Retention grant design']
    },
    {
      id: 'executive',
      no: '02',
      icon: Users,
      title: 'Executive Pay & IPO Readiness',
      description:
        'C-suite pay design that survives scrutiny. Peer group construction, NRC papers, and the disclosure groundwork a listing demands — done before the bankers ask.',
      features: ['C-suite pay design', 'IPO & listing readiness', 'Board and NRC papers', 'Governance & disclosure']
    },
    {
      id: 'variable',
      no: '03',
      icon: Target,
      title: 'Variable Pay & Sales Compensation',
      description:
        'Quotas, accelerators, caps, clawbacks. Payout curves get pressure-tested against last year’s actuals, so the plan pays for performance rather than for luck.',
      features: ['STI & bonus plan design', 'Quota and commission models', 'Payout curve modelling', 'CRM & payout integration']
    },
    {
      id: 'benefits',
      no: '04',
      icon: Shield,
      title: 'Employee Benefits Design',
      description:
        'Medical, life and accident cover, retirals, wellness, family support and flexible allowances. Costed, benchmarked, and weighted toward what people actually use.',
      features: ['Health & insurance cover', 'Retirals & financial wellbeing', 'Family and caregiving support', 'Flexible benefit allowances']
    },
    {
      id: 'benchmarking',
      no: '05',
      icon: BarChart3,
      title: 'Pay Structures & Benchmarking',
      description:
        'Job architecture, grade bands and market data you can defend in a room full of sceptics. Pay equity audits included, plus the philosophy document that settles next year’s argument.',
      features: ['Job evaluation & levelling', 'Salary bands and ranges', 'Market benchmarking', 'Pay equity audits']
    },
    {
      id: 'rewards',
      no: '06',
      icon: Star,
      title: 'Performance & Merit Systems',
      description:
        'Merit matrices, rating distributions, and appraisal forms managers will actually finish. We brief the managers too — a good matrix applied badly is still a bad outcome.',
      features: ['Merit increase matrices', 'Appraisal & rating design', 'Total rewards statements', 'Manager enablement']
    }
  ]

  // Stats removed - new company focus on value proposition

  // Testimonials removed - using whitepaper philosophy instead

  // Industries and achievements removed - focus on comprehensive HR services and current capabilities

  const facts = [
    { value: '50–500', label: 'Headcount range where our work lands hardest' },
    { value: '06', label: 'Reward disciplines under one roof' },
    { value: '30 min', label: 'First conversation. No obligation.' }
  ]

  const sectors = [
    {
      icon: Zap,
      title: 'Technology & Startups',
      copy: 'Equity-heavy packages, headcount doubling mid-cycle, and bands that have to hold while the org chart moves.'
    },
    {
      icon: DollarSign,
      title: 'Financial Services',
      copy: 'Deferral, malus and clawback under a regulator’s eye. Incentives that reward the right kind of risk.'
    },
    {
      icon: Building,
      title: 'Professional Services',
      copy: 'Partner economics, utilisation-linked pay, and reward models built around client outcomes rather than headcount.'
    }
  ]

  const differentiators = [
    {
      icon: Shield,
      title: 'Senior people, no pyramid',
      copy: 'The consultant who scopes your work is the consultant who does it. Nothing gets handed down a bench.'
    },
    {
      icon: Users,
      title: 'We stay past the design',
      copy: 'Design is the easy half. We stay through communication, manager briefings, and the first cycle that runs on the new plan.'
    },
    {
      icon: TrendingUp,
      title: 'Big-firm training',
      copy: 'Decades built inside Fortune 500 reward functions, top-tier consultancies, and startups that scaled fast and messily.'
    }
  ]

  // Get page content from Firestore
  const pageContent = content.pages?.home || {}
  const heroTitle = pageContent.hero?.title || 'Strategic Total Rewards That Drive Business Success'
  const heroSubtitle = pageContent.hero?.subtitle || 'We partner with mid-size companies and growing startups to design, implement, and optimize compensation and benefits programs that attract top talent, drive performance, and align with your business strategy.'

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400 font-open-sans">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black">
      <SEO
        title="Total Rewards Consulting - Comprehensive Compensation & Benefits Solutions"
        description="Complete total rewards consulting including equity compensation & LTIs, executive compensation & IPO readiness, variable pay & sales compensation, comprehensive employee benefits, compensation consulting, and performance management. Transform your total rewards strategy."
        keywords="total rewards consulting, equity compensation, long term incentives, executive compensation, IPO readiness, variable pay, sales compensation, comprehensive employee benefits, compensation consulting, market benchmarking, performance management, ESOP design, stock options, salary structure design"
      />

      {/* Hero Section */}
      <section className="pt-14 lg:pt-16 pb-20 lg:pb-28 text-white relative overflow-hidden min-h-screen flex items-center grain">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/hero-bg-compressed.webp" type="image/webp" />
          <img
            src="/hero-bg-compressed.jpg"
            alt="Modern office meeting room representing professional total rewards consulting"
            className="absolute inset-0 hero-bg-image"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette that
            pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-80 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(120% 82% at 50% 6%, transparent 26%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.92) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-full px-5 py-2.5 mb-10"
              variants={fadeInUp}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]" aria-hidden="true"></span>
              <span className="text-mono-label text-primary-300">
                Total Rewards Advisory
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle.includes('People. Purpose. Pay.') ? (
                <>
                  <span className="text-gold-metal">People. Purpose. Pay.</span>
                  <br />
                  Perfectly Aligned.
                </>
              ) : (
                heroTitle
              )}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-10 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-14"
              variants={fadeInUp}
            >
              <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                Book a 30-minute call
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                See what we do
              </Link>
            </motion.div>

            <motion.div
              className="max-w-3xl mx-auto text-center"
              variants={fadeInUp}
            >
              <div className="surface-raised-gold edge-lit sheen rounded-card-lg px-8 py-9">
                <p className="text-xl lg:text-2xl text-primary-300 font-semibold mb-4 font-open-sans tracking-tight text-balance">
                  &ldquo;Benchmarking breeds sameness. Strategy demands difference.&rdquo;
                </p>
                <p className="text-neutral-300 text-base font-open-sans text-balance">
                  Most pay programmes copy the market. The ones worth having decide where to break from it.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Facts rail — pressed into the page rather than floating on it */}
      <section className="bg-black relative overflow-hidden py-14 lg:py-20" aria-label="At a glance">
        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="surface-inset rounded-card-lg border-x border-b border-white/5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            {facts.map((fact) => (
              <div key={fact.label} className="px-8 py-8 text-center sm:text-left">
                <div className="text-3xl lg:text-4xl font-bold text-primary-400 font-open-sans tracking-tight mb-2">
                  {fact.value}
                </div>
                <div className="text-body-small text-neutral-400">{fact.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The stack — WebGL object paired with the argument for it. */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold" aria-labelledby="stack-heading">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <Reveal>
              <p className="text-mono-label text-primary-400 mb-5">
                <span className="text-neutral-500">02 / 05</span>
                <span className="text-neutral-600 mx-2">—</span>
                The architecture
              </p>
              <h2 id="stack-heading" className="text-section-title text-white mb-6 text-balance">
                Four layers. One decision.
              </h2>
              <p className="text-body-large text-neutral-300 mb-6">
                Base pay sets the floor. Benefits hold people through the parts of life
                that have nothing to do with work. Variable pay moves behaviour inside
                a year. Equity ties someone to an outcome years out.
              </p>
              <p className="text-body text-neutral-400 mb-8">
                Most organisations tune these separately, in different quarters, owned by
                different people. That is where the money leaks — not in any single layer,
                but in the seams between them.
              </p>
              <Link to="/services" className="btn-accent sheen">
                See how we work on each
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </Reveal>

            <Reveal delay={110}>
              <RewardsStack3D className="w-full aspect-square max-w-[560px] mx-auto" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-16 lg:mb-20">
            <p className="text-mono-label text-primary-500 mb-5 flex items-center gap-3">
              <span>01 / 04</span>
              <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
              <span>What we build</span>
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Six disciplines. One reward architecture.
            </h2>
            <p className="text-body-large text-neutral-300">
              Equity, executive pay, incentives, benefits, structures and merit are usually run by
              different people who never compare notes. We treat them as one system, because that is
              how an employee experiences them.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Reveal key={service.id} className="scene h-full" delay={index * 80}>
                {/* No `.sheen` here — its overflow:hidden would flatten
                    preserve-3d and cancel the .tilt-raise lift. */}
                <TiltCard className="surface-raised edge-lit rounded-card h-full group">
                  {/* depth-layer keeps the 3D context alive so the icon plate can
                      sit proud of the card face. The copy stays on the surface —
                      translateZ rasterises text at a fractional scale and softens it. */}
                  <div className="depth-layer p-8 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-7">
                      <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center tilt-raise">
                        <service.icon className="w-6 h-6 text-primary-400" aria-hidden="true" />
                      </div>
                      <span
                        className="text-mono-label text-neutral-600 group-hover:text-primary-500/70 transition-colors duration-500"
                        aria-hidden="true"
                      >
                        {service.no} / 06
                      </span>
                    </div>

                    <h3 className="text-heading-1 text-white mb-4 text-balance">{service.title}</h3>
                    <p className="text-body-small text-neutral-400 mb-7">{service.description}</p>

                    <ul className="space-y-2.5 mb-8 mt-auto">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-body-small text-neutral-300">
                          <span className="w-1 h-1 mt-2.5 rounded-full bg-primary-500 flex-shrink-0" aria-hidden="true"></span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="hairline mb-6" aria-hidden="true"></div>

                    <Link
                      to={`/services#${service.id}`}
                      className="inline-flex items-center text-mono-label text-primary-400 hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black rounded-sm"
                    >
                      <span>
                        Learn more<span className="sr-only"> about {service.title}</span>
                      </span>
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          {/* Structure block — the band diagram sits opposite the copy it
              illustrates, and flips to a single column below lg. */}
          <div className="mt-20 lg:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:order-2">
              <p className="text-mono-label text-primary-400 mb-5">The spine of it</p>
              <h3 className="text-display-3 text-white mb-6 text-balance">
                Structure is where the other five get settled.
              </h3>
              <p className="text-body text-neutral-300 mb-5">
                Grades, ranges and midpoints decide what an offer can be, what a merit
                increase costs, and where an equity grant starts. Without them, every one
                of those conversations reopens from scratch.
              </p>
              <p className="text-body-small text-neutral-400 mb-8">
                Ranges widen as scope widens, and adjacent grades overlap deliberately —
                so a strong performer can out-earn a new joiner a grade above without the
                structure breaking.
              </p>
              <Link
                to="/services#benchmarking"
                className="inline-flex items-center text-mono-label text-primary-400 hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black rounded-sm"
              >
                <span>Pay structures &amp; benchmarking</span>
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </Reveal>

            <Reveal delay={110} className="lg:order-1">
              <div className="surface-raised edge-lit rounded-card-lg p-5 sm:p-7 lg:p-8 overflow-x-auto">
                <BandArchitecture className="w-full h-auto min-w-[560px]" />
              </div>
              <p className="text-body-small text-neutral-500 mt-4 text-center">
                Illustrative structure, indexed to the lowest midpoint — not client data.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16 lg:mb-20">
            <Reveal className="max-w-3xl">
              <p className="text-mono-label text-primary-500 mb-5 flex items-center gap-3">
                <span>02 / 04</span>
                <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
                <span>Why a boutique</span>
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">
                Large-firm rigour without the large-firm bench.
              </h2>
              <p className="text-body-large text-neutral-300 mb-6">
                Big consultancies sell you a partner and staff the work with analysts. We are small on
                purpose. Fewer clients, senior hands on every file, and the agility a large bench
                cannot buy.
              </p>
              <p className="text-body text-neutral-400">
                Same three pieces every time — the people, the structure, the money. What changes is
                how they are arranged for your business.
              </p>
            </Reveal>

            <Reveal delay={110}>
              <BrandPrimitives
                variant="cluster"
                className="w-full aspect-square max-w-[420px] mx-auto"
                label="Cube, sphere and cone in the brand palette, gathered as one close cluster — the small senior team that stays on a single file."
              />
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
            {differentiators.map((item, index) => (
              <Reveal key={item.title} delay={index * 90}>
                <div className="surface-raised edge-lit rounded-card p-8 h-full">
                  <div className="w-12 h-12 surface-raised-gold rounded-xl flex items-center justify-center mb-6">
                    <item.icon className="w-5 h-5 text-primary-400" aria-hidden="true" />
                  </div>
                  <h3 className="text-heading-1 text-white mb-4">{item.title}</h3>
                  <p className="text-body-small text-neutral-400">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <Reveal>
              <h3 className="text-display-3 text-white mb-8">Where we know the terrain</h3>
              <div className="space-y-4">
                {sectors.map((sector) => (
                  <div
                    key={sector.title}
                    className="flex items-start gap-5 surface-raised rounded-card p-6"
                  >
                    <div className="w-12 h-12 gradient-premium-gold rounded-xl flex items-center justify-center flex-shrink-0 premium-shadow">
                      <sector.icon className="w-5 h-5 text-navy-950" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2 text-lg font-open-sans">{sector.title}</h4>
                      <p className="text-body-small text-neutral-400">{sector.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={110}>
              <div className="surface-raised-gold edge-lit rounded-card-lg p-8 lg:p-10">
                <h3 className="text-heading-1 text-white mb-5">Our sweet spot</h3>
                <p className="text-body text-neutral-300 mb-8">
                  Companies between 50 and 500 people, at the point where informal pay decisions stop
                  scaling and a real structure has to replace them.
                </p>

                <div className="space-y-3 mb-9">
                  {[
                    'Mid-size firms outgrowing spreadsheet pay decisions',
                    'Startups heading into a priced round or a listing',
                    'Boards that want the pay story defensible before it is public'
                  ].map((line) => (
                    <div key={line} className="flex items-start gap-3 surface-inset rounded-lg px-4 py-3">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0" aria-hidden="true"></span>
                      <span className="text-body-small text-neutral-300">{line}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/about"
                  className="inline-flex w-full items-center justify-center rounded-md px-6 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-primary-500/25 transition-colors duration-300 hover:border-primary-500/70 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                >
                  Meet the team
                  <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HR Services Philosophy Section */}
      <section className="section-padding gradient-premium text-white relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
            <p className="text-mono-label text-primary-500 mb-6 flex items-center justify-center gap-3">
              <span>03 / 04</span>
              <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
              <span>How we think</span>
            </p>
            <h2 className="text-display-lg text-white mb-7 text-balance">People. Purpose. Pay.</h2>
            <p className="text-body-large text-neutral-300">
              <strong className="text-white">Benchmarking breeds sameness, but strategy demands difference.</strong>{' '}
              Our framework connects three questions that most reward reviews answer separately, and
              then wonders why the programme pulls in three directions.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                no: 'I',
                icon: Users,
                title: 'People',
                question: 'Who enables that value?',
                copy: 'We identify the roles and capabilities that actually move the number, and separate them from the ones the org chart merely flatters.'
              },
              {
                no: 'II',
                icon: Target,
                title: 'Purpose',
                question: 'Why do we exist? What do we uniquely create?',
                copy: 'Rewards get anchored to the mission and the identity, which is what tells you where contribution genuinely occurs.'
              },
              {
                no: 'III',
                icon: DollarSign,
                title: 'Pay',
                question: 'How do we recognise them?',
                copy: 'Compensation is designed around outcomes rather than inputs. That is the difference between a cost line and a strategic lever.'
              }
            ].map((pillar, index) => (
              <Reveal key={pillar.title} delay={index * 100}>
                <div className="surface-raised edge-lit rounded-card-lg p-8 lg:p-10 h-full text-center">
                  <span className="block text-mono-label text-primary-500/70 mb-6">
                    {pillar.no}
                  </span>
                  <div className="w-14 h-14 gradient-premium-gold rounded-2xl flex items-center justify-center mx-auto mb-6 premium-shadow">
                    <pillar.icon className="w-6 h-6 text-navy-950" aria-hidden="true" />
                  </div>
                  <h3 className="text-heading-1 text-white mb-3">{pillar.title}</h3>
                  <p className="text-body-small text-primary-300/90 italic mb-4">
                    &ldquo;{pillar.question}&rdquo;
                  </p>
                  <p className="text-body-small text-neutral-400">{pillar.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120} className="mt-16 lg:mt-20 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <BrandPrimitives
                variant="row"
                className="w-full aspect-square max-w-[340px] mx-auto"
                label="Cone, sphere and cube in the brand palette, set out in a single row — purpose, people and pay held on one line."
              />
              <p className="text-body-large text-neutral-300 text-balance">
                Read them in order. Purpose defines where value is created, people are the ones
                who create it, and pay is how that contribution gets recognised. Answer one
                without the other two and you have a policy, not a strategy.
              </p>
            </div>
          </Reveal>

          <Reveal delay={140} className="mt-16 lg:mt-20 text-center max-w-3xl mx-auto">
            <div className="hairline mb-10" aria-hidden="true"></div>
            <blockquote className="text-body-large text-neutral-200 italic mb-6 text-balance">
              &ldquo;You cannot buy commitment. But you can reward contribution that flows from shared mission.&rdquo;
            </blockquote>
            <p className="text-primary-400 font-semibold font-open-sans">— Manit Mittal, Chief Executive Officer</p>
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding gradient-premium relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(70% 60% at 50% 100%, rgba(255,209,24,0.09) 0%, transparent 70%)'
          }}
        />

        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12 text-center relative">
          <Reveal className="mb-10 lg:mb-12">
            <BrandPrimitives
              variant="column"
              className="w-full aspect-square max-w-[260px] mx-auto"
              label="Sphere, cone and cube in the brand palette, stacked in a single column — one reward architecture built up in layers."
            />
          </Reveal>

          <Reveal>
            <p className="text-mono-label text-primary-500 mb-6 flex items-center justify-center gap-3">
              <span>04 / 04</span>
              <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
              <span>Where to start</span>
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Tell us what is broken in your pay programme.
            </h2>
            <p className="text-body-large text-neutral-300 mb-12 max-w-2xl mx-auto">
              Bring the merit cycle that ran over budget, the grant table nobody can explain, or the
              band structure that stopped holding. Half an hour, and you will leave with a view on it
              either way.
            </p>
          </Reveal>

          <Reveal delay={90}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
              <Link to="/contact" className="btn-accent sheen text-base px-10 py-4">
                Book a 30-minute call
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/resources"
                className="inline-flex items-center justify-center rounded-md px-10 py-4 text-base font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                Read our insights
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              'Free 30-minute consultation',
              'No obligation',
              'A specialist’s read on it'
            ].map((item, index) => (
              <Reveal key={item} delay={140 + index * 80}>
                <div className="flex flex-col items-center gap-3 surface-inset rounded-card p-6 h-full">
                  <CheckCircle className="w-7 h-7 text-primary-400" aria-hidden="true" />
                  <span className="text-neutral-200 font-medium text-body-small font-open-sans">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
